import React, { useState } from 'react';
import { WizardContext } from '../wizard/wizardProvider';
import { ActionContext } from '../Actions/Action';
import { getBasicPageTourViewOnlySteps } from '../Actions/actions_wizards/basic_page_tour';
import { PageLockContext } from '../PageLockProvider';
//import { getAddingAnActionSteps } from '../Actions/actions_wizards/adding_an_action';

interface ActionPageProps {
    showWizardLinks?: boolean;
    setDialogOpen?: React.Dispatch<React.SetStateAction<boolean>>;
}

const ActionPage: React.FC<ActionPageProps> = ({ showWizardLinks, setDialogOpen }) => {
    const [selectedHelpPage, setSelectedHelpPage] = useState(showWizardLinks ? "wizard" : "shortcuts");

    function getContent(name: string): JSX.Element {
        switch (name) {
            case "wizard":
                return <Wizards setDialogOpen={setDialogOpen} />
            case "shortcuts":
                return <Shortcuts />
            case "newAction":
                return <NewAction />
            case "filters":
                return <Filters />
            case "search":
                return <Search />
            case "export":
                return <Export />
            default:
                return <Shortcuts />
        }
    }

    return (
        <div className='flex flex-row'>
            <div className="flex flex-col min-w-[200px] mr-2">
                <ul className="text-center">
                    {showWizardLinks && (
                        <li className={(selectedHelpPage == "wizard" ? "bg-table-subheader " : "bg-table-header ") + "rounded-tl-md p-2 bg-table-header hover:cursor-pointer "} onClick={() => setSelectedHelpPage("wizard")}>Wizards</li>
                    )}
                    <li className={(selectedHelpPage == "shortcuts" ? "bg-table-subheader " : "bg-table-header ") + (showWizardLinks ? "" : "rounded-tl-md ") + "p-2 bg-table-header hover:cursor-pointer "} onClick={() => setSelectedHelpPage("shortcuts")}>Keyboard Shortcuts</li>
                    <li className={(selectedHelpPage == "newAction" ? "bg-table-subheader " : "bg-table-header ") + "p-2 bg-table-header hover:cursor-pointer "} onClick={() => setSelectedHelpPage("newAction")}>Creating a new Action</li>
                    <li className={(selectedHelpPage == "search" ? "bg-table-subheader " : "bg-table-header ") + "p-2 hover:cursor-pointer "} onClick={() => setSelectedHelpPage("search")}>Search</li>
                    <li className={(selectedHelpPage == "filters" ? "bg-table-subheader " : "bg-table-header ") + "p-2 bg-table-header hover:cursor-pointer "} onClick={() => setSelectedHelpPage("filters")}>Table Filters</li>
                    <li className={(selectedHelpPage == "export" ? "bg-table-subheader " : "bg-table-header ") + "rounded-bl-md p-2 bg-table-header hover:cursor-pointer "} onClick={() => setSelectedHelpPage("export")}>Exporting Data</li>
                </ul>
            </div>
            <div className="flex flex-col h-[800px] p-2 overflow-y-auto scrollbar">{getContent(selectedHelpPage)}</div>
        </div>
    );
};

export default ActionPage;

interface WizardsProps {
    setDialogOpen?: React.Dispatch<React.SetStateAction<boolean>>;
}

const Wizards: React.FC<WizardsProps> = ({ setDialogOpen }) => {
    const lock = React.useContext(PageLockContext);
    const wizard = React.useContext(WizardContext);
    const action = React.useContext(ActionContext);

    function onWizardComplete() {
        action.setDemoMode(false);
        action.setDialogOpen?.(false);
    }

    function startGuidedTour() {
        action.setDemoMode(true);
        wizard.setWizardSteps(getBasicPageTourViewOnlySteps({ viewOnly: lock?.viewOnly, actionContext: action }));
        wizard.setGuidedTourCompleteFunctionCall(() => () => onWizardComplete());
        wizard.startGuidedTour();
        setDialogOpen?.(false);
    }

    // function startAddingAnActionTour() {
    //     action.setDemoMode(true);
    //     wizard.setWizardSteps(getAddingAnActionSteps({ actionContext: action }));
    //     wizard.setGuidedTourCompleteFunctionCall(() => () => onWizardComplete());
    //     wizard.startGuidedTour();
    //     setDialogOpen?.(false);
    // }

    function openDemoMode() {
        action.setDemoMode(true);
        setDialogOpen?.(false);
    }

    return (
        <div className='flex flex-col'>
            <div className='flex flex-row p-2 mb-2'>
                <div className='flex flex-col mr-4'>
                    <button onClick={startGuidedTour} className="ml-2 button-default">Start Guided Tour</button>
                </div>

                <div className='flex flex-col'>
                    <p>
                        The Guided Tour Wizard simply explains the various sections of the Actions page and what information is required.
                    </p>
                    <p>
                        It does not create any Actions for you, it is simply a guide to help you understand how to use the Actions page.
                    </p>
                    <p>
                        This Tour is for when the page is in view only mode.
                    </p>
                </div>
            </div>
            <div className='flex flex-row p-2 mb-2'>
                <div className='flex flex-col mr-4'>
                    <button onClick={openDemoMode} className="ml-2 button-default">Enter Demo Mode</button>
                </div>

                <div className='flex flex-col'>
                    <p>
                        Demo mode allows you to try out the Actions page without making any actual changes to the data.
                    </p>
                    <p>
                        The table will be populated with some sample data and you can try out adding, editing, deleting and exporting Actions.
                    </p>
                </div>
            </div>
            {/* <div className='flex flex-row p-2 mb-2'>
                <div className='flex flex-col mr-4'>
                    <button onClick={startAddingAnActionTour} className="ml-2 px-2 py-1 bg-blue-500 text-white rounded-md w-40">Start Add An Action Wizard</button>
                </div>

                <div className='flex flex-col'>
                    <p>
                        The Add An Action Wizard takes you through the process of adding a new action step by step.
                    </p>
                    <p>
                        By default the page will be in demo mode to allow you to try out adding an action without affecting real data.
                    </p>
                    <p>
                        However if you wish to add a real action, at the start of the wizard, exit demo mode and continue with the wizard.
                    </p>
                </div>
            </div> */}
        </div>
    );
};

const NewAction: React.FC = () => {
    return (
        <>
            <h1 className="text-2xl font-bold mb-2">Creating a new Action</h1>
            <div className="flex flex-row">
                <video width="960" height="720" controls>
                    <source src="/videos/help/actions/Adding_actions.mp4" type="video/mp4" />
                </video>
                <div className="p-2">
                    <p>
                        Add a new action by pressing the "Ctrl + R" keyboard shortcut or by clicking the "Add Action" button in the top right corner.
                    </p>
                    <br />
                    <p>
                        A new row will be added to the top of the table with some default values
                    </p>
                    <br />
                    <p>
                        Edit the fields as required.
                    </p>
                    <br />
                    <p>
                        Changes are saved automatically.
                    </p>
                </div>
            </div>
        </>
    );
}

const Shortcuts: React.FC = () => {
    return (
        <>
            <h1 className="text-2xl font-bold mb-2">Keyboard Shortcuts</h1>
            <table className="table-auto border-collapse border border-slate-400">
                <thead>
                    <tr>
                        <th className="border border-slate-300 p-2">Shortcut</th>
                        <th className="border border-slate-300 p-2">Action</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td className="border border-slate-300 p-2">Ctrl + R</td>
                        <td className="border border-slate-300 p-2">Add a new row (Action)</td>
                    </tr>
                    <tr>
                        <td className="border border-slate-300 p-2">Ctrl + B</td>
                        <td className="border border-slate-300 p-2">Hide / Show Sidebar</td>
                    </tr>
                    <tr>
                        <td className="border border-slate-300 p-2">Ctrl + J</td>
                        <td className="border border-slate-300 p-2">Open the 'Jump' menu to navigate quickly</td>
                    </tr>
                </tbody>
            </table>
        </>
    );
}

const Search: React.FC = () => {
    return (
        <>
            <h1 className="text-2xl font-bold mb-2">Search</h1>
            <div className="flex flex-row">
                <video width="960" height="720" controls>
                    <source src="/videos/help/general/table_global_search.mp4" type="video/mp4" />
                </video>
                <div className="p-2">
                    <p>
                        You can search the table using the global search bar located above the table.
                    </p>
                    <br />
                    <p>
                        The global search will search all text fields in the table.
                    </p>
                </div>
            </div>
        </>
    );
}

const Filters: React.FC = () => {
    return (
        <>
            <h1 className="text-2xl font-bold mb-2">Table Filters</h1>
            <div className="flex flex-row">
                <video width="960" height="720" controls>
                    <source src="/videos/help/general/table_column_filters.mp4" type="video/mp4" />
                </video>
                <div className="p-2">
                    <p>
                        Each column in the table can be filtered via the control in the column header.
                    </p>
                </div>
            </div>
        </>
    );
}

const Export: React.FC = () => {
    return (
        <>
            <h1 className="text-2xl font-bold mb-2">Exporting Data</h1>
            <div className="flex flex-row">
                <video width="960" height="720" controls>
                    <source src="/videos/help/general/table_export_pdf.mp4" type="video/mp4" />
                </video>
                <div className="p-2">
                    <p>
                        You can export the data in the table to a CSV or PDF file by clicking the "Export" button in the top right corner of the table.
                    </p>
                    <br />
                    <p>
                        The exported data will reflect any filters or searches that have been applied to the table, as well as the current column order.
                    </p>
                </div>
            </div>
        </>
    );
}